import * as React from 'react'
import { Check, ChevronDown } from 'lucide-react'
import { cn } from '@/lib/utils'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { PICKER_CLASSES } from './input'

export interface MultiSelectOption {
  /** The value carried in state. */
  value: string
  label: string
  /** Options sharing a group are listed together under its name, in first-seen order. */
  group?: string
  /** Shown but not changeable — say, the last of a set that must keep one. */
  disabled?: boolean
  /** Why a disabled option can't be changed, shown beside it. */
  disabledReason?: string
}

/** An option's label, with its group's name when the label alone is ambiguous. */
function nameOf(option: MultiSelectOption, options: readonly MultiSelectOption[]): string {
  const repeated = options.some((o) => o !== option && o.label === option.label && o.group !== option.group)
  return repeated && option.group !== undefined ? `${option.group} · ${option.label}` : option.label
}

/**
 * A form field that holds several choices: it reads as one field, naming what is
 * chosen, and opens a checklist. For a list too long to lay out as checkboxes
 * without the form becoming mostly checkboxes.
 *
 * Not a filter — that is `FilterMenu`, which belongs in a table's header band.
 * This sits in a `Field`, which labels it through `id`.
 */
export function MultiSelect({
  id,
  options,
  selected,
  onToggle,
  placeholder = 'Choose…',
  summary,
  disabled = false,
  className,
}: {
  id?: string
  options: readonly MultiSelectOption[]
  selected: readonly string[]
  onToggle: (value: string) => void
  placeholder?: string
  /**
   * What the closed field says. By default the chosen labels in list order, each
   * named with its group when another group has an option of the same name.
   */
  summary?: (chosen: readonly MultiSelectOption[]) => string
  disabled?: boolean
  className?: string
}) {
  const [open, setOpen] = React.useState(false)
  const chosen = options.filter((o) => selected.includes(o.value))
  const text = chosen.length === 0 ? null : summary ? summary(chosen) : chosen.map((o) => nameOf(o, options)).join(', ')

  const groups: { name: string | undefined; options: MultiSelectOption[] }[] = []
  for (const option of options) {
    const group = groups.find((g) => g.name === option.group)
    if (group) group.options.push(option)
    else groups.push({ name: option.group, options: [option] })
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          id={id}
          type="button"
          disabled={disabled}
          aria-haspopup="listbox"
          className={cn(PICKER_CLASSES, 'justify-between', className)}
        >
          <span className={cn('min-w-0 truncate', text === null && 'text-ink-subtle')}>{text ?? placeholder}</span>
          <ChevronDown className="h-4 w-4 shrink-0 text-ink-muted" />
        </button>
      </PopoverTrigger>
      {/* Stays open while options are picked: choosing several is the point. */}
      <PopoverContent align="start" className="w-[var(--radix-popover-trigger-width)] min-w-60 p-1">
        {/* Scrolls rather than growing; a popover taller than the window can't be closed. */}
        <div role="listbox" aria-multiselectable="true" className="max-h-72 overflow-y-auto">
          {groups.map((group) => (
            <div key={group.name ?? ''} role="group" aria-label={group.name}>
              {group.name !== undefined ? (
                <p className="m-0 px-2 pb-1 pt-2 text-xs font-medium uppercase text-ink-muted">{group.name}</p>
              ) : null}
              {group.options.map((option) => {
                const on = selected.includes(option.value)
                return (
                  <button
                    key={option.value}
                    type="button"
                    role="option"
                    aria-selected={on}
                    disabled={option.disabled}
                    onClick={() => onToggle(option.value)}
                    className={cn(
                      'flex min-h-8 w-full items-center gap-2 rounded-sm px-2 py-1 text-left text-sm outline-none transition-colors focus-visible:shadow-[inset_0_0_0_2px_var(--ds-focus)] disabled:cursor-not-allowed disabled:opacity-60',
                      on ? 'bg-accent-soft font-medium text-accent-ink' : 'text-ink hover:bg-surface-hover',
                    )}
                  >
                    <span className="grid h-4 w-4 shrink-0 place-items-center">
                      {on ? <Check className="h-3.5 w-3.5" /> : null}
                    </span>
                    <span className="min-w-0">{option.label}</span>
                    {option.disabled && option.disabledReason ? (
                      <span className="ml-auto shrink-0 text-xs font-normal text-ink-muted">
                        {option.disabledReason}
                      </span>
                    ) : null}
                  </button>
                )
              })}
            </div>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  )
}
